import { Injectable } from '@angular/core';
import { concatMap, tap } from 'rxjs/operators';
import { forkJoin, Observable, of } from 'rxjs';
import { CoreModule } from '../../core.module';
import { ServerConnectionService } from '../server-connection.service';
import { UserService } from './user.service';
import { StripeService } from '../stripe.service';
import { SavedPaymentMethod } from '../../../types/local';
import { SavedPaymentMethodsAPIResponse } from '../../../types/responses';

@Injectable({
	providedIn: CoreModule,
})
export class SavedPaymentsMethodsService {
	store: SavedPaymentMethod[];

	unchanged = false;

	author;

	constructor(
		private userService: UserService,
		private scs: ServerConnectionService,
		private stripeService: StripeService,
	) {
		this.userService.author$.subscribe((author) => {
			this.author = author;
		});
	}

	public create(paymentMethod: SavedPaymentMethod) {
		this.unchanged = false;
		return this.scs.http$('POST', '/api/saved-payment-methods', null, paymentMethod);
	}

	public list(userId: string = this.author?._id): Observable<SavedPaymentMethodsAPIResponse> {
		if (this.store && this.unchanged) {
			return of({ success: true, savedPaymentMethods: this.store });
		}

		if (!userId) {
			return of({ success: true, savedPaymentMethods: [] });
		}

		return this.scs.get$<SavedPaymentMethodsAPIResponse>(`/api/saved-payment-methods/${userId}`)
			.pipe(tap((data) => {
				this.store = data.savedPaymentMethods;
				this.unchanged = true;
			}));
	}

	public remove(paymentMethodId: string) {
		this.unchanged = false;
		return this.scs.http$('POST', '/api/saved-payment-methods/remove', null, { paymentMethodId });
	}

	public getSavedCards() {
		if (!this.author) {
			return of([]);
		}
		return forkJoin({
			savedPaymentMethodsResult: this.list(this.author._id),
			stripeCards: this.stripeService.getCards(this.author.stripeCustomerId),
		}).pipe(concatMap(({ savedPaymentMethodsResult, stripeCards }: { savedPaymentMethodsResult: any; stripeCards: any }) => {
			if (savedPaymentMethodsResult && stripeCards?.card?.cards?.data?.length) {
				const savedPaymentMethodIds = savedPaymentMethodsResult.savedPaymentMethods
					.map((savedPaymentMethod) => savedPaymentMethod.paymentMethodId);

				const applicableCards = stripeCards.card.cards.data.filter((card) => savedPaymentMethodIds.includes(card.id));
				return of(applicableCards);
			}

			return of([]);
		}));
	}
}
